import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { eur, fmtDate, Loading, EmptyState, PaymentBadge } from "@/components/common";

const _now = new Date();
const CURRENT_MONTH = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}`;

export default function AdminRicavi() {
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get(`/admin/ricavi?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [month]);
  useEffect(load, [load]);

  const shift = (d) => {
    const [y, m] = month.split("-").map(Number);
    const dt = new Date(y, m - 1 + d, 1);
    setMonth(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/ricavi/csv?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ricavi_${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV scaricato.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const monthLabel = new Date(`${month}-15T12:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  if (!data) return <div className="card-soft p-6"><Loading /></div>;

  return (
    <section className="card-soft p-5 sm:p-6 space-y-5" data-testid="admin-ricavi">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-navy">Ricavi per mese</h2>
        <button data-testid="ricavi-export" onClick={exportCsv} disabled={busy}
          className="btn-secondary !py-2 text-sm inline-flex items-center gap-2 min-h-[44px] disabled:opacity-50">
          <Download size={16} /> Esporta CSV
        </button>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <button data-testid="ricavi-prev" onClick={() => shift(-1)} aria-label="Mese precedente"
          className="p-3 min-h-[44px] min-w-[44px] rounded-full border border-[#dbe7f3] bg-white text-[#1e3a5f]">
          <ChevronLeft size={20} />
        </button>
        <p className="font-semibold text-navy capitalize" data-testid="ricavi-month-label">{monthLabel}</p>
        <button data-testid="ricavi-next" onClick={() => shift(1)} disabled={month >= CURRENT_MONTH} aria-label="Mese successivo"
          className="p-3 min-h-[44px] min-w-[44px] rounded-full border border-[#dbe7f3] bg-white text-[#1e3a5f] disabled:opacity-40">
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="text-center">
          <p className="font-serif text-2xl sm:text-3xl text-navy" data-testid="ricavi-totale">{eur(data.totale_incassato)}</p>
          <p className="text-xs text-muted-soft">Incassato nel mese</p>
        </div>
        <div className="text-center">
          <p className="font-serif text-2xl sm:text-3xl text-navy" data-testid="ricavi-count">{data.n_incassi}</p>
          <p className="text-xs text-muted-soft">Pagamenti ricevuti</p>
        </div>
        <div className="text-center">
          <p className="font-serif text-2xl sm:text-3xl text-navy" data-testid="ricavi-orders">{data.orders.length}</p>
          <p className="text-xs text-muted-soft">Ordini del mese</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="ricavi-breakdown">
        {Object.entries(data.per_tipo).map(([k, v]) => (
          <span key={k} className="text-xs font-semibold text-[#1e3a5f] bg-[#f0f7ff] rounded-full px-3 py-1.5" data-testid={`ricavi-tipo-${k.replace(/\s+/g, "-")}`}>
            {k}: {eur(v)}
          </span>
        ))}
        {Object.entries(data.per_metodo).map(([k, v]) => (
          <span key={k} className="text-xs font-semibold text-[#2f5d3e] bg-[#e8f0eb] rounded-full px-3 py-1.5 capitalize">
            {k}: {eur(v)}
          </span>
        ))}
      </div>

      {data.orders.length === 0 ? (
        <EmptyState title="Nessun ordine in questo mese" />
      ) : (
        <>
          <div className="space-y-3 lg:hidden">
            {data.orders.map((p) => (
              <div key={p.id} className="border border-[#f1f5f9] rounded-xl p-4 space-y-1.5" data-testid={`ricavi-order-m-${p.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <p className="text-navy font-medium">{p.customer_name}</p>
                  <PaymentBadge status={p.status} method={p.method} />
                </div>
                <p className="text-sm text-body">{p.servizio || p.tipo}</p>
                <p className="text-xs text-muted-soft">{fmtDate(p.created_at)} · <span className="capitalize">{p.method}</span> · {p.tipo}</p>
                <p className="font-semibold text-navy">{eur(p.amount)}</p>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto hidden lg:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-soft border-b border-[#e2e8f0]">
                  <th className="p-3">Data</th><th className="p-3">Cliente</th><th className="p-3">Servizio</th><th className="p-3">Tipo</th><th className="p-3">Metodo</th><th className="p-3">Importo</th><th className="p-3">Stato</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((p) => (
                  <tr key={p.id} className="border-b border-[#f1f5f9] last:border-0" data-testid={`ricavi-order-${p.id}`}>
                    <td className="p-3 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                    <td className="p-3 text-navy font-medium">{p.customer_name}</td>
                    <td className="p-3">{p.servizio || p.tipo}</td>
                    <td className="p-3">{p.tipo}</td>
                    <td className="p-3 capitalize">{p.method}</td>
                    <td className="p-3 font-semibold text-navy whitespace-nowrap">{eur(p.amount)}</td>
                    <td className="p-3"><PaymentBadge status={p.status} method={p.method} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p className="text-xs text-muted-soft">
        L'incassato conta solo i pagamenti effettivamente confermati nel mese; l'elenco mostra tutti gli ordini creati nel mese, in qualsiasi stato.
      </p>
    </section>
  );
}
